import { Routes, Route, Navigate } from 'react-router-dom';
import { Login } from './components/Login';
import { CompleteRegistration } from './components/CompleteRegistration';
import { AdminDashboard } from './components/AdminDashboard';
import { ContractsPage } from './components/ContractsPage';
import { ContractDetails } from './components/ContractDetails';
import { AuditLogs } from './components/AuditLogs';
import { UsersPage } from './components/UsersPage';
import { TransactionsPage } from './components/TransactionsPage';
import { ForecastPage } from './components/ForecastPage';
import { PerformancePage } from './components/PerformancePage';
import { PointsDashboardPage } from './components/PointsDashboardPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Unauthorized } from './components/Unauthorized';
import { useAuth } from './contexts/AuthContext';
import SyncStatusPage from './components/SyncStatusPage';
import ClientDashboard from './components/ClientDashboard';
import ClientDeliverables from './components/ClientDeliverables';
import ClientTodos from './components/ClientTodos';
import ClientFinalDeliverables from './components/ClientFinalDeliverables';
import ClientResources from './components/ClientResources';
import ClientFinancials from './components/ClientFinancials';
import ClientLayout from './components/ClientLayout';
import ClientLanding from './components/ClientLanding';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import SupaTestLogin from './components/SupaTestLogin';
import ResetPassword from './pages/ResetPassword';
import StatusPage from './components/StatusPage';
import ReportsList from './components/ReportsList';
import ReportDetail from './components/ReportDetail';

const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  
  // Redirect based on role
  if (user.role === 'admin' || user.role === 'team_member') {
    return <Navigate to="/admin" replace />;
  } else if (user.role === 'client') {
    return <Navigate to="/client" replace />;
  }
  
  // Default fallback
  return <Navigate to="/unauthorized" replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/complete-registration" element={<CompleteRegistration />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Admin/Team Member Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <ReportsList />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/:id" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <ReportDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contracts" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <ContractsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contracts/:contractId" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <ContractDetails />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/performance" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <PerformancePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/status" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <StatusPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/points-dashboard" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <PointsDashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/transactions" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <TransactionsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/forecast" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <ForecastPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/audit" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <AuditLogs />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/users" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UsersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/syncing" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member']}>
            <SyncStatusPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Client Landing Route */}
      <Route 
        path="/client" 
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientLanding />
          </ProtectedRoute>
        } 
      />
      
      {/* Client View Routes - NESTED */}
      <Route 
        path="/client-view/:contractId" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'team_member', 'client']}>
            <ClientLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<ClientDashboard />} />
        <Route path="deliverables" element={<ClientDeliverables />} />
        <Route path="todos" element={<ClientTodos />} />
        <Route path="final-deliverables" element={<ClientFinalDeliverables />} />
        <Route path="resources" element={<ClientResources />} />
        <Route path="financials" element={<ClientFinancials />} />
        {/* Default/fallback route */}
        <Route index element={<ClientDashboard />} />
      </Route>
      
      <Route path="/" element={<RoleBasedRedirect />} />
      <Route path="*" element={<RoleBasedRedirect />} />
      <Route path="/supatest" element={<SupaTestLogin />} />
    </Routes>
  );
};

export default AppRoutes; 